import { Injectable } from '@angular/core';
import { catchError, forkJoin, Observable, of } from 'rxjs';
import { AllergyResponse } from '../../models/allergy.interface';
import { sectionName, SoapNote } from '../../models/dictation.interface';
import { MedicationRequest } from '../../models/medical-request.interfcae';
import { HistoricalDataService } from '../historical-data/historical-data.service';

export interface CdmPlanRow {
  relevantConditions: string;
  patientLifestyleGoals: string;
  providerTreatmentsAndActions: string;
  reviewNotes: string;
}

export interface CdmHealthScreeningRow {
  action: string;
  lastCompleted: string;
}

export interface CdmSourceData {
  consultations: SoapNote[];
  allergies: AllergyResponse[];
  medications: MedicationRequest[];
}

export interface CdmMappedResponse {
  eligibility: string;
  eligibilityReason: string;
  annualSmartGoal: string;
  planRows: CdmPlanRow[];
  healthScreeningRows: CdmHealthScreeningRow[];
}

@Injectable({
  providedIn: 'root',
})
export class CdmPlanService {
  constructor(private readonly historicalDataService: HistoricalDataService) {}

  loadSourceData$(patientId: string): Observable<CdmSourceData> {
    this.historicalDataService.resetDateRange();
    const { startDate, endDate } = this.historicalDataService.getNextRange();

    return forkJoin({
      consultations: this.historicalDataService
        .getPreviousConsultationByPatientId$(patientId, startDate, endDate)
        .pipe(catchError(() => of([] as SoapNote[]))),
      allergies: this.historicalDataService
        .getAllergiesByPatientId$(patientId)
        .pipe(catchError(() => of([] as AllergyResponse[]))),
      medications: this.historicalDataService
        .getMedicationsForPatient$(patientId, startDate, endDate)
        .pipe(catchError(() => of([] as MedicationRequest[]))),
    });
  }

  buildPayload(sourceData: CdmSourceData, conversation: string): Record<string, unknown> {
    const consultations = sourceData.consultations.map((consultation) => ({
      date: consultation.soapNoteDate || '',
      summary: this.buildConsultationSummaryHtml(consultation),
    }));

    const allergies = sourceData.allergies.map((allergy) => ({
      value: allergy.allergen?.display || '',
      status: allergy.clinicalStatus?.code || '',
      description: allergy.note || '',
      onSetDate: allergy.onsetDate || '',
    }));

    const medication = sourceData.medications.map((medicationRequest) => ({
      medicationText: medicationRequest.medicationText || '',
      authoredOn: medicationRequest.authoredOn || '',
      dosageInstruction: (medicationRequest.dosageInstructions || [])
        .map((instruction) => instruction?.timingText || instruction?.routeText || instruction?.method || instruction?.note || '')
        .filter((value) => !!value),
    }));

    return {
      conversation,
      history: [
        {
          type: 'historical consultation summary',
          consultations,
        },
        {
          type: 'allergies history',
          allergies,
        },
        {
          type: 'medication history',
          medication,
        },
      ],
      type: 'cdm',
      version: '1',
      model: 'gemini',
    };
  }

  mapResponseToTable(response: unknown): CdmMappedResponse {
    const root =
      response && typeof response === 'object' && 'response' in (response as Record<string, unknown>)
        ? (response as { response: unknown }).response
        : response;

    const annualGoal = this.deepFindValueByKeys(root, [
      "Patient's SMART goal for their health within the next 12 months",
      'annual smart goal',
      'smart goal',
      'patient annual smart goal',
      'goal',
    ]);

    const eligibility = this.deepFindValueByKeys(root, ['eligibility']);
    const eligibilityReason = this.deepFindValueByKeys(root, ['reason']);

    return {
      eligibility: this.normalizeCdmText(eligibility),
      eligibilityReason: this.normalizeCdmText(eligibilityReason),
      annualSmartGoal: this.normalizeCdmText(annualGoal),
      planRows: this.extractCdmRows(root),
      healthScreeningRows: this.buildHealthScreeningRows(root),
    };
  }

  private extractConsultationSectionHtml(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string') {
      return value.trim();
    }

    if (typeof value === 'object' && 'changingThisBreaksApplicationSecurity' in value) {
      return String((value as { changingThisBreaksApplicationSecurity: unknown }).changingThisBreaksApplicationSecurity || '').trim();
    }

    return '';
  }

  private buildConsultationSummaryHtml(consultation: SoapNote): string {
    const sections: unknown[] = [
      consultation[sectionName.subjective],
      consultation[sectionName.objective],
      consultation[sectionName.assessment],
      consultation[sectionName.plan],
    ];

    const htmlParts = sections
      .map((section) => this.extractConsultationSectionHtml(section))
      .filter((section) => !!section);

    return htmlParts.join('<hr/>');
  }

  private normalizeCdmText(value: unknown): string {
    if (value === null || value === undefined) {
      return '—';
    }

    if (typeof value === 'string') {
      const normalized = value.replace(/\s+/g, ' ').trim();
      return normalized || '—';
    }

    if (Array.isArray(value)) {
      const normalized = value
        .map((item) => this.normalizeCdmText(item))
        .filter((item) => item !== '—');
      return normalized.length ? normalized.join(', ') : '—';
    }

    if (typeof value === 'object') {
      const record = value as Record<string, unknown>;
      const direct = ['value', 'text', 'content', 'goal', 'summary', 'description']
        .map((key) => record[key])
        .find((item) => item !== undefined && item !== null);
      if (direct !== undefined) {
        return this.normalizeCdmText(direct);
      }
      return JSON.stringify(value);
    }

    return String(value);
  }

  private readValueByKeys(source: unknown, keys: string[]): unknown {
    if (!source || typeof source !== 'object') {
      return undefined;
    }

    const record = source as Record<string, unknown>;
    const normalizedEntries = Object.entries(record).map(([key, value]) => ({
      key: key.toLowerCase().replace(/[^a-z0-9]/g, ''),
      value,
    }));

    for (const key of keys) {
      const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
      const match = normalizedEntries.find((entry) => entry.key === normalizedKey || entry.key.includes(normalizedKey));
      if (match) {
        return match.value;
      }
    }

    return undefined;
  }

  private deepFindValueByKeys(source: unknown, keys: string[], visited: Set<unknown> = new Set<unknown>()): unknown {
    const direct = this.readValueByKeys(source, keys);
    if (direct !== undefined) {
      return direct;
    }

    if (!source || typeof source !== 'object' || visited.has(source)) {
      return undefined;
    }

    visited.add(source);

    if (Array.isArray(source)) {
      for (const item of source) {
        const found = this.deepFindValueByKeys(item, keys, visited);
        if (found !== undefined) {
          return found;
        }
      }
      return undefined;
    }

    for (const value of Object.values(source as Record<string, unknown>)) {
      const found = this.deepFindValueByKeys(value, keys, visited);
      if (found !== undefined) {
        return found;
      }
    }

    return undefined;
  }

  private collectCandidateRowObjects(source: unknown, sink: Array<Record<string, unknown>>, visited: Set<unknown> = new Set<unknown>()): void {
    if (!source || typeof source !== 'object' || visited.has(source)) {
      return;
    }

    visited.add(source);

    if (Array.isArray(source)) {
      const objectItems = source.filter((item) => item && typeof item === 'object') as Array<Record<string, unknown>>;
      if (objectItems.length > 0) {
        sink.push(...objectItems);
      }
      for (const item of source) {
        this.collectCandidateRowObjects(item, sink, visited);
      }
      return;
    }

    const record = source as Record<string, unknown>;
    for (const value of Object.values(record)) {
      this.collectCandidateRowObjects(value, sink, visited);
    }
  }

  private extractCdmRows(source: unknown): CdmPlanRow[] {
    const carePlanSource = this.deepFindValueByKeys(source, ['carePlan']);
    if (Array.isArray(carePlanSource)) {
      const carePlanRows = carePlanSource.map((item) => {
        const row = (item || {}) as Record<string, unknown>;
        return {
          relevantConditions: this.normalizeCdmText(row['relevantCondition']),
          patientLifestyleGoals: this.normalizeCdmText(row['goals']),
          providerTreatmentsAndActions: this.normalizeCdmText(row['treatmentAction']),
          reviewNotes: this.normalizeCdmText(row['reviewNotes'] ?? row['review']),
        };
      });

      const meaningfulCarePlanRows = carePlanRows.filter((row) => [
        row.relevantConditions,
        row.patientLifestyleGoals,
        row.providerTreatmentsAndActions,
        row.reviewNotes,
      ].some((value) => value !== '—'));

      if (meaningfulCarePlanRows.length > 0) {
        return meaningfulCarePlanRows;
      }
    }

    const rowSource: Array<Record<string, unknown>> = [];
    this.collectCandidateRowObjects(source, rowSource);

    if (source && typeof source === 'object' && !Array.isArray(source)) {
      rowSource.push(source as Record<string, unknown>);
    }

    const rows = rowSource
      .filter((row) => !!row)
      .map((row) => ({
        relevantConditions: this.normalizeCdmText(this.deepFindValueByKeys(row, ['relevant conditions in this plan'])),
        patientLifestyleGoals: this.normalizeCdmText(this.deepFindValueByKeys(row, ['patient lifestyle goals'])),
        providerTreatmentsAndActions: this.normalizeCdmText(this.deepFindValueByKeys(row, ['provider treatments & patient actions'])),
        reviewNotes: this.normalizeCdmText(this.deepFindValueByKeys(row, ['review 3-6 monthly. have goals been met? any further actions required?'])),
      }));

    const meaningfulRows = rows.filter((row) =>
      [row.relevantConditions, row.patientLifestyleGoals, row.providerTreatmentsAndActions, row.reviewNotes].some((value) => value !== '—')
    );

    if (meaningfulRows.length === 0) {
      const fallbackRow: CdmPlanRow = {
        relevantConditions: this.normalizeCdmText(this.deepFindValueByKeys(source, ['relevant conditions in this plan'])),
        patientLifestyleGoals: this.normalizeCdmText(this.deepFindValueByKeys(source, ['patient lifestyle goals'])),
        providerTreatmentsAndActions: this.normalizeCdmText(this.deepFindValueByKeys(source, ['provider treatments & patient actions'])),
        reviewNotes: this.normalizeCdmText(this.deepFindValueByKeys(source, ['review 3-6 monthly. have goals been met? any further actions required?'])),
      };

      const hasFallbackData = Object.values(fallbackRow).some((value) => value !== '—');
      if (hasFallbackData) {
        return [fallbackRow];
      }
    }

    return meaningfulRows.length
      ? meaningfulRows
      : [{
          relevantConditions: '—',
          patientLifestyleGoals: '—',
          providerTreatmentsAndActions: '—',
          reviewNotes: '—',
        }];
  }

  private buildHealthScreeningRows(source: unknown): CdmHealthScreeningRow[] {
    const screeningKeyMap: Array<{ action: string; keys: string[] }> = [
      { action: 'Health Assessment', keys: ['healthAssessment'] },
      { action: 'Vaccines', keys: ['vaccines'] },
      { action: 'CST', keys: ['cst'] },
      { action: 'Mammogram', keys: ['mammogram'] },
      { action: 'FOBT', keys: ['fobt'] },
      { action: 'Bone Density Scan', keys: ['boneDensityScan'] },
      { action: 'PSA', keys: ['psa'] },
      { action: 'DMMR', keys: ['dmmr'] },
    ];

    return screeningKeyMap.map((item) => {
      const value = this.deepFindValueByKeys(source, [
        ...item.keys,
        `${item.action} last completed`,
        `last completed ${item.action}`,
      ]);
      return {
        action: item.action,
        lastCompleted: this.normalizeCdmText(value),
      };
    });
  }
}
